/**
 * ★ **窗口一变，就把 Pixi 滤镜栈里那几个"上一帧的输入贴图"清掉。**
 *
 * ## 病在哪
 *
 * `FilterSystem` 的 `_filterStack` 是**跨帧复用**的：每一层滤镜一个 `FilterData`，
 * 帧与帧之间不重建。`_findFilterResolution()` 里读的偏偏是**刚刚 push 出来的
 * 那一个 slot**（也就是这个 slot 上一帧留下的 `inputTexture`），而不是"上一层
 * 这一帧的输入"：
 *
 * ```js
 * // pixi.js 8.21, FilterSystem._findFilterResolution()
 * let currentIndex = this._filterStackIndex - 1;
 * while (currentIndex > 0 && this._filterStack[currentIndex].skip) --currentIndex;
 * return currentIndex > 0 && this._filterStack[currentIndex].inputTexture
 *   ? this._filterStack[currentIndex].inputTexture.source._resolution
 *   : rootResolution;
 * ```
 *
 * 平时那张贴图还活着（纹理池里的），读到的分辨率只是**过时**、无害。
 * 可是 `renderer.resize()` → `ViewSystem._updateScreenSize()` →
 * `TexturePool.setScreenSize()` → `_pruneScreenTextures()` 会把**旧尺寸的池贴图
 * 销毁**（`texture.destroy(true)`，`source` 变 `null`），那个 slot 里的引用就成
 * 了一把空壳 —— 下一次**嵌套** push（我们这里：`world` 上的故障滤镜 + 背景/濒死
 * 层自己的滤镜）直接抛 `Cannot read properties of null (reading '_resolution')`。
 *
 * ## 后果为什么是整屏黑
 *
 * 异常从 `renderer.render()` 里抛出来，那一帧剩下的指令全没跑；之后每一帧都在
 * 同一个地方抛 → 画面停在黑上，**只有刷新页面才恢复**。而且窗口一变就必现：
 * "进过对局（濒死时挂上过故障滤镜）→ 回标题 → 拖窗口 → 再进濒死"。
 *
 * ## 修法
 *
 * `renderer` 的 `'resize'` 事件是在贴图已经销毁**之后**发的（`resize()` 里
 * 先 `view.resize()` 再 `emit('resize')`），所以在那之后把栈里所有
 * `inputTexture` 置 `null` —— `_findFilterResolution()` 对 `null` 有守卫，
 * 会退回根分辨率（本来就是它该给的值）。
 *
 * ⚠️ 这是**绕上游的 bug**，不是我们的结构问题：升级/降级 `pixi.js` 时请重新
 * 看一眼 `_findFilterResolution()`，如果它改成读"上一层"就把它删掉，
 * 并在 `docs/background-shader.md` 里记一笔。
 */

/** `FilterSystem` 里我们唯一关心的形状（私有字段，只能结构化地摸） */
interface FilterStackSlot {
  inputTexture: unknown;
}

interface WithFilterStack {
  readonly _filterStack?: readonly (FilterStackSlot | undefined)[];
}

/**
 * 把滤镜栈里所有 slot 的 `inputTexture` 忘掉（置 `null`）。
 *
 * @returns 真的清掉了几处 —— 冒烟和测试拿它当"这条接线还在不在"的证据。
 */
export function forgetFilterStackInputs(subject: unknown): number {
  const stack = (subject as WithFilterStack | null | undefined)?._filterStack;
  if (!Array.isArray(stack)) return 0;
  let cleared = 0;
  for (const slot of stack) {
    if (slot === undefined || slot === null) continue;
    if (slot.inputTexture === null || slot.inputTexture === undefined) continue;
    slot.inputTexture = null;
    cleared += 1;
  }
  return cleared;
}
